import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React from 'react';
import {CreateSupportPlanTaskContext} from '../state/support-plan-page-state';
import {useSupportTaskCreateDrawerState} from '../state/support-task-create-drawer-state';
import {UniversalInput} from '../../shared/UniversalInput';
import LazyAutocomplete from './LazyAutocomplete';

type Props = {
  initialContext: CreateSupportPlanTaskContext | null
  organizationId: string
  supportPlanId: string
  isDrawerOpen: boolean
  onDrawerClosed: () => void
  refreshTasks: () => Promise<void>
}

export const SupportTaskCreateDrawer = ({organizationId, supportPlanId, isDrawerOpen, onDrawerClosed, refreshTasks, initialContext}: Props) => {
  const state = useSupportTaskCreateDrawerState(organizationId, supportPlanId, initialContext);

  const buildTicket = () => <LazyAutocomplete
    totalCount={state.ticketsData?.total ?? 0}
    pageSize={20}
    fetchOptions={(params) => state.fetchTicketsOptions(params.page, params.search, params.pageSize)}
    value={state.formData.ticketId ? ({id: state.formData.ticketId, name: state.ticketsData?.items?.find(it => it.id === state.formData.ticketId)?.title ?? 'Nie znaleziono'}) : null}
    onChange={(option) => {
      if(option) {
        state.setFormData({...state.formData, ticketId: option.id, ticketName: option.name})
      } else {
        state.setFormData({...state.formData, ticketId: null, ticketName: null})
      }
    }}
    label="Wybierz zgłoszenie (opcjonalne)"
  />

  const buildUserMultiSelect = () => (
    <Autocomplete
      multiple
      options={state.users}
      getOptionLabel={(option) => option.email}
      value={state.formData.users || []}
      onChange={(_, selected) => {
        state.setFormData({
          ...state.formData,
          users: selected,
        });
      }}
      disableCloseOnSelect
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option.email}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Uczestnicy"
          placeholder="Wybierz uczestników"
        />
      )}
      fullWidth
    />
  );


  const buildTitle = () => <UniversalInput
    label="Nazwa"
    type="text"
    value={state.formData.title}
    onChange={(val) => state.setFormData({...state.formData, title: val as string})}
  />

  const buildDescription = () => <UniversalInput
    label="Opis"
    type="textarea"
    value={state.formData.description}
    onChange={(val) => state.setFormData({...state.formData, description: val as string})}
  />

  const buildTaskTypeOptions = (): string[] => {
    if(initialContext === null) {
      return ['ANALYSIS', 'CONSULTATION', 'LONG_TERM_TASK']
    }

    if(initialContext.slot.assignmentType === 'CONSULTATION') return ['CONSULTATION']
    if(initialContext.slot.assignmentType === 'ANALYSIS') return ['ANALYSIS']
    if(initialContext.slot.assignmentType === 'GENERAL') return ['ANALYSIS', 'CONSULTATION', 'LONG_TERM_TASK']

    return ['ANALYSIS', 'CONSULTATION', 'LONG_TERM_TASK']
  }

  const buildTaskType = () => <FormControl fullWidth>
    <InputLabel id="demo-simple-select-label">Typ zadania</InputLabel>
    <Select
      labelId="demo-simple-select-label"
      id="demo-simple-select"
      value={state.formData.taskType}
      label={'Typ zadania'}
      onChange={event => {
        if(event.target.value !== 'CONSULTATION') {
          state.setFormData({...state.formData, taskType: event.target.value, users: []})
        } else {
          state.setFormData({...state.formData, taskType: event.target.value})
        }
      }}
    >
      {
        buildTaskTypeOptions().map(it => <MenuItem value={it}>{state.translate(`support.plan.task.type.${it}`)}</MenuItem>)
      }
    </Select>
  </FormControl>

  const buildMeetingLink = () => <UniversalInput
    label="Link do spotkania"
    type="text"
    value={state.formData.meetingLink}
    onChange={(val) => state.setFormData({...state.formData, meetingLink: val as string})}
  />

  const buildStartDate = () => <UniversalInput
    label="Data rozpoczecia pracy"
    type="date"
    value={state.formData.startDate}
    onChange={(val) => {
      if(state.formData.taskType === 'CONSULTATION') {
        state.setFormData({...state.formData, startDate: val as string, endDate: val as string})
      } else {
        state.setFormData({...state.formData, startDate: val as string})
      }
    }}
    min={0}
    step={1}
  />

  const buildEndDate = () => <UniversalInput
    label="Data zakonczenia pracy"
    type="date"
    disabled={state.formData.taskType === 'CONSULTATION'}
    value={state.formData.endDate}
    onChange={(val) => state.setFormData({...state.formData, endDate: val as string})}
    min={0}
    step={1}
  />

  const buildStartTime = () => <UniversalInput
    label="Godzina rozpoczecia pracy (opcjonalne)"
    type="time"
    value={state.formData.startTime}
    onChange={(val) => state.setFormData({...state.formData, startTime: val as string})}
    min={0}
    step={1}
  />

  const buildEndTime = () => <UniversalInput
    label="Godzina zakonczenia pracy (opcjonalne)"
    type="time"
    value={state.formData.endTime}
    onChange={(val) => state.setFormData({...state.formData, endTime: val as string})}
    min={0}
    step={1}
  />

  const buildPlannedHours = () =>  <TextField
    label={'Liczba godzin'}
    type="number"
    value={state.formData.plannedHours}
    onChange={(e) => {
      const value = Number(e.target.value)
      if (value >= 0) {
        state.setFormData({...state.formData, plannedHours: value})
      }
    }}
    fullWidth
    inputProps={{
      min: 0,
    }}
    InputLabelProps={{
      shrink: true,
    }}
  />

  return <Drawer
    anchor="right"
    open={isDrawerOpen}
    onClose={onDrawerClosed}
    PaperProps={{
      sx: {
        width: 480,
        marginTop: '64px',
        height: `calc(100% - 64px)`,
        position: 'fixed',
        right: 0,
        padding: 3,
        overflowY: 'auto',
      }
    }}
  >
    <form onSubmit={async (event) => {
      event.preventDefault();
      await state.handleSubmit()
      await refreshTasks()
      onDrawerClosed();
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, mt: 0.5 }}>
        <CalendarTodayIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Dodaj nowe zadanie
        </Typography>
      </Box>
      <Stack spacing={3}>
        {buildTicket()}
        {buildTitle()}
        {buildDescription()}
        {buildTaskType()}
        {state.formData.taskType === 'CONSULTATION' ? buildMeetingLink() : <></>}
        {state.formData.taskType === 'CONSULTATION' ? buildUserMultiSelect() : <></>}
        {buildStartDate()}
        {buildEndDate()}
        {buildStartTime()}
        {buildEndTime()}
        {buildPlannedHours()}
        <Button type="submit"
                variant="contained"
                color="primary"
        >
          {state.translate('common.save.title')}
        </Button>
        <Button variant="outlined"
                onClick={onDrawerClosed}
        >
          {state.translate('common.cancel.title')}
        </Button>
      </Stack>
    </form>
  </Drawer>;
};